/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { HardHat, Loader2, AlertTriangle } from "lucide-react";
import { fmtD } from "../../../lib/usDate";

import { T } from "../../../lib/i18n";
/* 【2026-10-30 输入侧时区提示】计划开工日是现场（美国）的开工日，
   中国同事按北京日历挑会挑到隔壁那天，所以按美国日历日填。 */
import { UsDateHint } from "./UsDateHint";

// 移交施工确认：可同时指派工程 PM 与计划开工日期（直接移交 / 审批通过两条路径共用）
// yellowCard（可选）：项目详情带下来的「许可证 / Job Card」附件摘要。传了就在**打开时**预检，
// 缺配置要求的凭证直接红框拦住；不传（审批中心那条路）则交给后端 400 兜底。
//
// 口径（2026-10-17 21:00 用户改为可配置 + 术语纠正）：
//   「把黄卡撤出移交门禁，或者做成可配置的配置……如果配置需要，就需要才能提交」
//   配置来自 `/design/v3/settings/handoff-gate`（项目管理 → 产物时间设置 → 移交资料），
//   默认只要求许可证。**显示名也读配置**，主管可以把「Job Card」改成自己的叫法。
//   ⚠️ 前端只做**预检提示**，真正的门禁在后端 `permit_guard`（配置改了即时生效）。
const DEFAULT_REQ = { permit: true, job_card: false };
const DEFAULT_LABELS = { permit: "施工许可证（Building Permit）", job_card: "现场检查卡（Job Card）" };

const cardKinds = (list) => {
  const kinds = new Set((list || []).map((f) => f.kind || "permit"));
  return { permit: kinds.has("permit") || kinds.has("both"), jobCard: kinds.has("job_card") || kinds.has("both") };
};

export const HandoffDialog = ({ address, buildingApproved = true, yellowCard, approvalId, projectId, comment, api, onClose, onDone, designOnly = false }) => {
  const [pms, setPms] = useState(null);
  const [pm, setPm] = useState("");
  const [start, setStart] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permitForce, setPermitForce] = useState(false);
  /* 【2026-10-26】纯设计项目（design_only）要转移交时的显式确认。
     用户口径：「公司是有纯设计项目的...不走工程...但是结项了也要留移交工程的口子」。
     默认 False —— 不勾选时后端仍按原语义拒绝（design_only_no_handoff），
     所以这个开关不是「放宽门禁」，而是「把业务决定记录下来」：
     只有操作人明确表示客户已改为委托本公司施工，才允许越过「不进工程部」这条默认规则。 */
  const [convertFromDesignOnly, setConvertFromDesignOnly] = useState(false);
  const [cardMissing, setCardMissing] = useState(null);
  const [missingKinds, setMissingKinds] = useState(null);
  const [preview, setPreview] = useState(null);
  const [req, setReq] = useState(null);  // 门禁配置；null = 还没拿到（按默认口径预检）
  const [labels, setLabels] = useState(DEFAULT_LABELS);
  useEffect(() => { api.get("/design/v3/eng-pms").then(({ data }) => setPms(data.pms || [])).catch(() => setPms([])); }, []); // eslint-disable-line
  useEffect(() => {
    api.get("/design/v3/settings/handoff-gate")
      .then(({ data }) => { setReq(data.require || DEFAULT_REQ); setLabels({ ...DEFAULT_LABELS, ...(data.labels || {}) }); })
      .catch(() => setReq(DEFAULT_REQ));
  }, []); // eslint-disable-line
  useEffect(() => { api.get(`/kickoff/preview?${projectId ? `designId=${projectId}&` : ""}address=${encodeURIComponent(address || "")}`).then(({ data }) => setPreview(data)).catch(() => setPreview(null)); }, [projectId, address]); // eslint-disable-line
  const loading = pms === null;
  // 按配置决定要哪几类；两项都不勾 = 不做文件预检（与后端「没勾选就不需要」一致）。
  const need = req || DEFAULT_REQ;
  const cardKnown = Array.isArray(yellowCard);
  const kinds = cardKinds(yellowCard);
  const needKinds = designOnly ? [] : ["permit", "job_card"].filter((k) => need[k]);
  /* 【2026-10-26】纯设计项目转移交不做凭证预检。
     原因：纯设计的阶段模板（DESIGN_ONLY_STAGES）不含 building_approval / permit_handoff，
     许可证附件在结构上不可能存在；后端 `_handoff` / `v3_handoff_direct` 对这条路
     已按同一条件豁免门禁（客户原来自己报建，改为委托后报建本就属于工程部
     pre-construction 的工作）。前端若继续按配置预检，确认按钮会永久置灰、
     这个口子等于不存在 —— 所以后端与前端必须同条件，缺一不可。 */
  const localMiss = cardKnown && !designOnly ? needKinds.filter((k) => (k === "permit" ? !kinds.permit : !kinds.jobCard)) : [];
  const localMissing = localMiss.length > 0;
  // 后端仍会返回 cardMissing（本组件拿到的 yellowCard 与后端读的是同一份数据，
  // 纯设计项目下必然一致为空），这条豁免保证两边不会互相打架。
  const blocked = (localMissing || !!cardMissing) && !designOnly;
  const miss = missingKinds || localMiss;
  const cardText = miss.length === 2
    ? T("移交前需要两份文件：① {0}；② {1}。", [labels.permit, labels.job_card])
    : miss[0] === "job_card"
      ? T("已有{0}，但缺少{1}的扫描件 —— 该卡片需张贴在工地现场，检查员到场必看，拍照或扫描上传即可。", [labels.permit, labels.job_card])
      : T("缺少{0}的批件。", [labels.permit]);
  const needForce = (!approvalId && !buildingApproved) || permitForce;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = approvalId
        ? await api.post(`/design/v3/approvals/${approvalId}/decide`, { approve: true, comment: comment || "", pm: pm || undefined, startDate: start || undefined })
        : await api.post(`/design/v3/projects/${projectId}/handoff`, { force, pm: pm || undefined, startDate: start || undefined, ...(designOnly ? { fromDesignOnly: convertFromDesignOnly } : {}) });
      const r = data.result || data;
      const ko = r.kickoff;
      toast.success(T("已移交施工{0}{1}{2}{3}，已通知工程部", [r.created ? T("，自动建立工程项目") : "", r.pm ? ` · PM ${r.pm}` : "", r.startDate ? T(" · 开工 {0}", [fmtD(r.startDate)]) : "", ko?.team ? T(" · 已自动分配 {0}", [ko.teamName]) : ko ? T(" · 新城市，待工程经理分配小组") : ""]));
      onDone?.(data);
    } catch (e) {
      // 后端 detail 现在有两种形状：字符串（老的 Permit 号 / Building 未批准）
      // 和 dict（{error, message, ...}，黄卡门禁用）。必须分开取，否则
      // `String(dict).includes("Permit")` 恒为 false、且 toast 会打出 [object Object]。
      const det = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
      const code = det && typeof det === "object" ? det.error : null;
      const msg = det && typeof det === "object" ? (det.message || det.messageEn || "") : (det || e.message);
      if (code === "yellow_card_missing") {
        setCardMissing(msg || T("移交资料不齐，无法移交"));
        setMissingKinds(Array.isArray(det.missingKinds) ? det.missingKinds : null);
      } else if (String(msg).includes("Permit")) setPermitForce(true);
      toast.error(msg);
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md p-5 space-y-4 shadow-2xl rounded-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dv3-handoff-dialog">
        <div className="flex items-start gap-2 flex-wrap md:flex-nowrap"><HardHat className="h-5 w-5 text-emerald-600 mt-0.5" /><div><p className="text-sm font-bold text-slate-900">{T("确认移交施工")}</p><p className="text-xs text-slate-500 mt-0.5" data-testid="dv3-handoff-address">{address}</p></div></div>
        <p className="text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5">{T("将匹配或自动建立工程项目并推进到")} <b>1-Pre-construction</b>{T("，通知工程经理 / 财务，并在工程项目中心生成「设计移交回执」。可在此直接指派项目经理与开工日期，让项目立即有人负责。")}</p>
        {blocked && <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg p-2.5 space-y-1" data-testid="dv3-handoff-card-missing" data-missing={(miss || []).join(",")} data-required={needKinds.join(",")}><p className="font-bold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{T("移交资料不齐，无法移交")}</p><p>{cardMissing || cardText}</p><p className="text-red-700">{T("上传位置：「阶段 → Building Approval + Permit」的 Permit 槽位。强制移交也不能跳过这一项。")}</p></div>}
        {needForce && !blocked && <label className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 cursor-pointer"><input type="checkbox" data-testid="dv3-handoff-force" checked={force} onChange={(e) => setForce(e.target.checked)} className="mt-0.5" /><span><AlertTriangle className="h-3.5 w-3.5 inline mr-1" />{permitForce ? T("尚未填写 Permit 号（应拿到 Permit 后再提交工程部），我确认强制移交") : T("Building 交付物尚未批准，我确认强制移交")}</span></label>}
        {preview && (
          <p className={`text-xs rounded-lg p-2.5 border ${preview.isNew ? "bg-red-50 border-red-200 text-red-800" : "bg-sky-50 border-sky-200 text-sky-900"}`} data-testid="dv3-handoff-team-preview" data-team={preview.team || ""}>
            {preview.isNew
              ? <>{T("城市「{0}」此前没有工程小组负责过 → 移交后将提示", [preview.city || T("未识别")])}<b>{T("工程经理手动分配小组")}</b>。</>
              : <>{T("系统将按城市「{0}」自动分配给", [preview.city])} <b>{preview.teamName}</b>{T("{0}，小组主界面将持续提示直到开始处理；超 2 天无动作短信 CEO 与项目成员。", [preview.source === "history" ? T("（该城市历史 {0} 个项目）", [preview.meta?.count]) : T("（手动规则）")])}</>}
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-slate-600 space-y-1"><span className="font-semibold">{T("工程项目经理（可选）")}</span>
            <select data-testid="dv3-handoff-pm" value={pm} disabled={loading} data-loaded={loading ? "0" : "1"} onChange={(e) => setPm(e.target.value)} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 disabled:opacity-60">{loading ? <option value="">{T("加载 PM 列表…")}</option> : <option value="">{T("稍后由工程经理指派")}</option>}{(pms || []).map((u) => <option key={u.username} value={u.username}>{u.name || u.username}{u.role === "eng_manager" ? T("（工程经理）") : ""}</option>)}</select></label>
          <label className="text-xs text-slate-600 space-y-1"><span className="font-semibold">{T("计划开工日期（可选）")}</span>
            <input type="date" data-testid="dv3-handoff-start" value={start} onChange={(e) => setStart(e.target.value)} className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5" />
            <UsDateHint testid="dv3-handoff-start-tz" className="!mt-1" /></label>
        </div>
        {/* 【2026-10-26】纯设计项目（只买图纸）转移交的显式确认。
            默认不勾 —— 不勾时后端仍按「设计单不进工程部」拒绝，不会静默放行。
            之所以单独做成一个必须手动勾的框：这是改变服务范围的业务决定（客户从
            「只要图纸」变成「也要我方施工」），不是普通流转，得留下明确的操作意图。 */}
        {designOnly && (
          <label className="flex items-start gap-2 text-xs text-emerald-900 bg-emerald-50 border border-emerald-300 rounded-lg p-2.5 cursor-pointer" data-testid="dv3-handoff-design-only-confirm-label">
            <input type="checkbox" data-testid="dv3-handoff-design-only-confirm" checked={convertFromDesignOnly} onChange={(e) => setConvertFromDesignOnly(e.target.checked)} className="mt-0.5" />
            <span><b>{T("转为本公司施工")}</b>{T("：这是纯设计项目（只买图纸），默认不进工程部。确认客户已改为委托本公司施工，据此转入工程部。")}</span>
          </label>
        )}
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
          <Button size="sm" variant="outline" data-testid="dv3-handoff-cancel" onClick={onClose} disabled={busy}>{T("取消")}</Button>
          <Button size="sm" data-testid="dv3-handoff-confirm" onClick={run} disabled={busy || loading || blocked || (needForce && !force) || (designOnly && !convertFromDesignOnly)} className="bg-emerald-600 hover:bg-emerald-700">{busy || loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><HardHat className="h-4 w-4 mr-1" />{T("确认移交")}</>}</Button>
        </div>
      </div>
    </div>
  );
};
